import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Ticket, Loader2, PlayCircle, CheckCircle2, LogIn } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/apiClient";
import { toast } from "sonner";

export default function ClaimSeat() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [claimable, setClaimable] = useState(null);
  const [claimed, setClaimed] = useState([]);
  const [busy, setBusy] = useState(false);

  const check = useCallback(() => { api.get("/me/claimable-seats").then((r) => setClaimable(r.data)).catch(() => setClaimable([])); }, []);
  useEffect(() => { if (!loading && user) check(); }, [loading, user, check]);

  const claim = async () => {
    setBusy(true);
    try { const { data } = await api.post("/me/claim-seats"); setClaimed(data.claimed); check(); if (data.claimed.length) toast.success(`Claimed ${data.claimed.length} course(s)!`); else toast.info("No seats to claim for your email."); }
    catch { toast.error("Could not claim seats"); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="py-32 grid place-items-center"><Loader2 className="w-8 h-8 animate-spin text-blue-800" /></div>;

  return (
    <div className="max-w-xl mx-auto px-4 sm:px-6 py-16">
      <div className="text-center mb-8">
        <span className="grid place-items-center w-16 h-16 rounded-2xl bg-blue-950 text-teal-400 mx-auto mb-4"><Ticket className="w-8 h-8" /></span>
        <h1 className="font-display text-3xl font-bold text-blue-950">Claim your training seat</h1>
        <p className="mt-2 text-slate-600">Your organization enrolled you in T5 Academy training. Claim your seat to start.</p>
      </div>

      {!user ? (
        <div className="bg-white rounded-2xl p-8 shadow-lg border border-blue-900/5 text-center">
          <p className="text-slate-600 mb-5">Sign in (or create an account) with <strong>the email your invite was sent to</strong>, then return here to claim.</p>
          <div className="flex gap-3 justify-center">
            <Button asChild className="bg-blue-900 text-white gap-2" data-testid="claim-login"><Link to="/login"><LogIn className="w-4 h-4" /> Sign in</Link></Button>
            <Button asChild variant="outline" data-testid="claim-register"><Link to="/register">Create account</Link></Button>
          </div>
        </div>
      ) : claimable === null ? (
        <div className="py-10 grid place-items-center"><Loader2 className="w-7 h-7 animate-spin text-blue-800" /></div>
      ) : (
        <div className="bg-white rounded-2xl p-8 shadow-lg border border-blue-900/5">
          <p className="text-sm text-slate-500 mb-4">Signed in as <strong className="text-blue-950">{user.email}</strong></p>
          {claimed.length > 0 && (
            <div className="mb-5 space-y-2" data-testid="claimed-list">
              {claimed.map((c) => (
                <div key={c.course_id} className="flex items-center justify-between bg-teal-50 rounded-xl px-4 py-3">
                  <span className="flex items-center gap-2 text-teal-900 text-sm"><CheckCircle2 className="w-4 h-4 text-teal-600" /> {c.course_title}</span>
                  {c.content_key && <Button asChild size="sm" className="bg-teal-600 hover:bg-teal-700 text-white gap-1" data-testid={`claim-start-${c.content_key}`}><Link to={`/course/${c.content_key}`}><PlayCircle className="w-4 h-4" /> Start</Link></Button>}
                </div>
              ))}
            </div>
          )}
          {claimable.length > 0 ? (
            <>
              <p className="text-blue-950 font-medium mb-2">Seats waiting for you:</p>
              <ul className="space-y-1.5 mb-5" data-testid="claimable-list">{claimable.map((c) => <li key={c.seat_id} className="text-sm text-slate-600 border-l-2 border-teal-500 pl-3">{c.course_title}</li>)}</ul>
              <Button onClick={claim} disabled={busy} data-testid="claim-btn" className="w-full h-12 bg-teal-600 hover:bg-teal-700 text-white">{busy ? <Loader2 className="w-5 h-5 animate-spin" /> : "Claim & start learning"}</Button>
            </>
          ) : (
            <div className="text-center">
              <p className="text-slate-500 mb-4">{claimed.length ? "All caught up — your courses are ready." : "No unclaimed seats found for this email. Ask your organization to assign you a seat, then refresh."}</p>
              <Button asChild variant="outline" data-testid="claim-dashboard"><Link to="/dashboard">Go to my dashboard</Link></Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
